import type { MerchantAddress, MerchantIdentity } from "./spec";

/**
 * Curated-list random generators backing the admin form's dice buttons.
 * Client-side only by design: the backend receives a fully concrete spec.
 */

const FIRST_NAMES = [
  "Casey",
  "Jordan",
  "Riley",
  "Avery",
  "Morgan",
  "Quinn",
  "Rowan",
  "Sage",
  "Emerson",
  "Finley",
  "Harper",
  "Kendall",
  "Logan",
  "Marley",
  "Noor",
  "Parker",
  "Reese",
  "Skyler",
  "Tatum",
  "Wren",
];

const LAST_NAMES = [
  "Almeida",
  "Bishop",
  "Chen",
  "Dubois",
  "Estrada",
  "Fontaine",
  "Gagnon",
  "Hassan",
  "Ivanov",
  "Jensen",
  "Kaur",
  "Lavoie",
  "Moreau",
  "Nguyen",
  "Okafor",
  "Petrov",
  "Quintero",
  "Rousseau",
  "Singh",
  "Tremblay",
];

const BUSINESS_FIRST = [
  "Maple",
  "Harbor",
  "Cedar",
  "Summit",
  "Willow",
  "Copper",
  "Granite",
  "Juniper",
  "Lakeside",
  "Northern",
  "Prairie",
  "Rustic",
  "Silver",
  "Sunset",
  "Urban",
  "Velvet",
  "Wandering",
  "Golden",
  "Blue Heron",
  "Iron",
];

const BUSINESS_SECOND = [
  "Coffee",
  "Barbershop",
  "Detailing",
  "Landscaping",
  "Catering",
  "Photography",
  "Plumbing",
  "Woodworks",
  "Bakery",
  "Fitness",
  "Tattoo",
  "Cleaning",
  "Electric",
  "Massage",
  "Pet Grooming",
  "Food Truck",
  "Records",
  "Florals",
  "Repair",
  "Studio",
];

const CA_ADDRESSES: MerchantAddress[] = [
  {
    line1: "123 Queen St W",
    city: "Toronto",
    state: "ON",
    postalCode: "M5H 2M9",
    country: "CA",
  },
  {
    line1: "456 Rue Sainte-Catherine",
    city: "Montreal",
    state: "QC",
    postalCode: "H3B 1A7",
    country: "CA",
  },
  {
    line1: "789 Granville St",
    city: "Vancouver",
    state: "BC",
    postalCode: "V6Z 1K3",
    country: "CA",
  },
  {
    line1: "321 8 Ave SW",
    city: "Calgary",
    state: "AB",
    postalCode: "T2P 1B4",
    country: "CA",
  },
  {
    line1: "654 Portage Ave",
    city: "Winnipeg",
    state: "MB",
    postalCode: "R3B 2G2",
    country: "CA",
  },
];

const US_ADDRESSES: MerchantAddress[] = [
  {
    line1: "123 Market St",
    city: "San Francisco",
    state: "CA",
    postalCode: "94103",
    country: "US",
  },
  {
    line1: "456 Congress Ave",
    city: "Austin",
    state: "TX",
    postalCode: "78701",
    country: "US",
  },
  {
    line1: "789 Peachtree St NE",
    city: "Atlanta",
    state: "GA",
    postalCode: "30308",
    country: "US",
  },
  {
    line1: "321 Pine St",
    city: "Seattle",
    state: "WA",
    postalCode: "98101",
    country: "US",
  },
  {
    line1: "654 W Randolph St",
    city: "Chicago",
    state: "IL",
    postalCode: "60661",
    country: "US",
  },
];

function pick<T>(items: readonly T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}

function randomDigits(count: number): string {
  return Array.from({ length: count }, () =>
    Math.floor(Math.random() * 10),
  ).join("");
}

function shortToken(): string {
  return Math.random().toString(36).slice(2, 6).padEnd(4, "0");
}

export function rollFirstName(): string {
  return pick(FIRST_NAMES);
}

export function rollLastName(): string {
  return pick(LAST_NAMES);
}

export function rollBusinessName(): string {
  return `${pick(BUSINESS_FIRST)} ${pick(BUSINESS_SECOND)}`;
}

/** Derives a synthetic, visually-identifiable dev email from a rolled name. */
export function emailFor(givenName: string, familyName: string): string {
  const clean = (s: string) => s.toLowerCase().replace(/[^a-z]/g, "");
  return `${clean(givenName)}.${clean(familyName)}.${shortToken()}@cpos-seed.test`;
}

export function rollPhone(country: "CA" | "US" = "CA"): string {
  const areaCode =
    country === "CA"
      ? pick(["416", "514", "604", "647"])
      : pick(["415", "512", "404", "206"]);
  return `+1${areaCode}555${randomDigits(4)}`;
}

export function rollAddress(country: "CA" | "US" = "CA"): MerchantAddress {
  return { ...pick(country === "CA" ? CA_ADDRESSES : US_ADDRESSES) };
}

export function rollDob(): { day: number; month: number; year: number } {
  return {
    day: 1 + Math.floor(Math.random() * 28),
    month: 1 + Math.floor(Math.random() * 12),
    year: 1960 + Math.floor(Math.random() * 45),
  };
}

/** Rolls a complete identity (no groups; callers set groups from the preset). */
export function rollIdentity(country: "CA" | "US" = "CA"): MerchantIdentity {
  const givenName = rollFirstName();
  const familyName = rollLastName();
  return {
    email: emailFor(givenName, familyName),
    password: "Pass123$",
    givenName,
    familyName,
    phone: rollPhone(country),
    dob: rollDob(),
    address: rollAddress(country),
    timezone: country === "CA" ? "America/Toronto" : "America/New_York",
    cognitoGroups: [],
  };
}
